import {
  useGetCurrentUserQuery,
  useGetPlaybookQuery,
  useRestorePlaybookMutation,
} from '@carboncopyco/data';
import {
  ArchiveIcon,
  DuplicateOutlineIcon,
  EditIcon,
  MoreVerticalOutlineIcon,
  OpenInNew1OutlineIcon,
  PlayOutlineIcon,
  RightOutlineIcon,
  ShareOutlineIcon,
  ShowOutlineIcon,
  SubdirectoryRightOutlineIcon,
} from '@carboncopyco/icons';
import {
  PermissionAction,
  PermissionKey,
  type Play,
  type Playbook,
  type Role,
} from '@carboncopyco/types';
import {
  Button,
  Dropdown,
  DropdownItem,
  Separator,
  SubMenu,
  Tooltip,
  Typography,
  notify,
} from '@carboncopyco/ui';
import { isDesktop, usePlaybookAnalytics } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { type ReactNode, isValidElement, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArchivePlaybookModal,
  DuplicatePlaybookModal,
} from '../../../../AssetActions/Playbook';
import { MovePlaybookModal } from '../../../../AssetActions/Playbook/modals/MovePlaybookModal';
import { DemoLinkPlaybookModal } from '../../../../DemoLinkModal';
import { UpdatePlaybookModal } from '../../../../PlaybookModal';
import { GiveADemoFlowModal } from '../../../../PresentationModals';
import {
  PlaybookGiveADemoTooltip,
  PlaybookPreviewTooltip,
} from '../../../../Tooltip';
import {
  useDesktopDeeplink,
  useFilesServiceContext,
  usePermissionsContext,
  useSearchParamAction,
} from '../../../../utils';
import { useWorkspacePageProps } from '../../WorkspacePageContext';
import { ArchiveTileDropdown } from '../ArchiveTileDropdown';
type PlaybookActionsDropdownProps = {
  playbook: Playbook;
  archived?: boolean;
  inFolder?: boolean;
  handleMenuOpen: (isOpen: boolean) => void;
  triggerElement?: ReactNode;
};

export const PlaybookActionsDropdown = ({
  playbook,
  archived,
  inFolder,
  handleMenuOpen,
  triggerElement,
}: PlaybookActionsDropdownProps) => {
  const {
    organizationId,
    workspaceId,
    desktopDomain,
    playPresentation,
    rootPath,
    goToPlaybookPreview,
  } = useWorkspacePageProps();
  const flags = useFlags();
  const navigate = useNavigate();
  const { getPermissions } = usePermissionsContext();
  const [isMenuOpen, { toggle: toggleMenu }] = useBoolean();
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [restorePlaybook] = useRestorePlaybookMutation();
  const [showDemoLinkModal, setShowDemoLinkModal] = useState(false);
  const shareExternallyRef = useRef<HTMLDivElement>();
  const {
    data: {
      user: { roles } = {},
    } = {},
  } = useGetCurrentUserQuery();
  const userRole: Role = roles?.[organizationId];
  const {
    trackPlaybookClickArchive,
    trackPlaybookClickUpdate,
    trackPlaybookClickDuplicate,
  } = usePlaybookAnalytics('ws_page_tile');
  const [moveModal, setMoveModal] = useState(false);
  const [showPlaybookModal, setShowPlaybookModal] = useState(false);
  const { id, readOnly, name } = playbook;
  const { data: playbookById } = useGetPlaybookQuery(
    { organizationId, workspaceId, id },
    { skip: archived || !isMenuOpen }
  );
  const [
    showGiveADemoModal,
    { setTrue: setShowGiveADemoModal, setFalse: setHideGiveADemoModal },
  ] = useBoolean();
  const { exeDeeplink, shouldShowDeeplink } = useDesktopDeeplink();

  const previewRef = useRef<HTMLDivElement>(null);
  const giveADemoRef = useRef<HTMLDivElement>(null);

  const deletePlaybookPermission = getPermissions(
    PermissionKey.playbook,
    PermissionAction.deletePlaybookById
  );
  const updatePlaybookPermission = getPermissions(
    PermissionKey.playbook,
    PermissionAction.updatePlaybookById
  );
  const duplicatePlaybookPermission = getPermissions(
    PermissionKey.playbook,
    PermissionAction.duplicatePlaybook
  );
  const movePlaybookPermission = getPermissions(
    PermissionKey.playbook,
    PermissionAction.movePlaybook
  );

  const handleOnPresent = async (play: Play) => {
    await playPresentation(play);
    setHideGiveADemoModal();
  };
  useSearchParamAction({
    param: 'update',
    action: () => {
      setShowPlaybookModal(true);
    },
    expectedValue: id.toString(),
  });

  const { filesServiceDomain } = useFilesServiceContext();
  return (
    <div onClick={(event) => event.stopPropagation()}>
      {archived ? (
        <ArchiveTileDropdown
          inFolder={inFolder}
          open={isMenuOpen}
          toggleOpen={() => {
            handleMenuOpen(!isMenuOpen);
            toggleMenu();
          }}
          preview={
            isDesktop()
              ? () => {
                  toggleMenu();
                  goToPlaybookPreview(id);
                }
              : undefined
          }
          restore={() => {
            restorePlaybook({
              id,
              workspaceId,
              organizationId,
            })
              .unwrap()
              .then((playbook: Playbook) => {
                notify.success(
                  `Playbook ${playbook.name} restored successfully`
                );
              });
          }}
        />
      ) : (
        <Dropdown
          triggerElement={
            isValidElement(triggerElement) ? (
              triggerElement
            ) : (
              <Button
                aria-label="playbook tile actions"
                small
                dark
                icon={<MoreVerticalOutlineIcon />}
              />
            )
          }
          width={200}
          asChild
          side="right"
          open={isMenuOpen}
          onOpenChange={() => {
            handleMenuOpen(!isMenuOpen);
            toggleMenu();
          }}
        >
          <div ref={previewRef}>
            <DropdownItem
              disabled={!isDesktop()}
              label="Preview"
              iconLeft={<ShowOutlineIcon size={20} />}
              onClick={() => goToPlaybookPreview(id)}
            />
          </div>
          <PlaybookPreviewTooltip
            desktopDomain={desktopDomain}
            previewRef={previewRef}
            location={location}
            extraNavigationPath={`playbook/${playbook.id}/overview/`}
            placement="right"
          />
          <div ref={giveADemoRef}>
            <DropdownItem
              label="Give a demo"
              iconLeft={<PlayOutlineIcon size={20} />}
              onClick={setShowGiveADemoModal}
            />
          </div>
          <PlaybookGiveADemoTooltip
            desktopDomain={desktopDomain}
            giveADemoRef={giveADemoRef}
            location={location}
            placement="right"
          />
          {!readOnly && updatePlaybookPermission && (
            <DropdownItem
              label="Update"
              iconLeft={<EditIcon size={20} />}
              iconRight={
                shouldShowDeeplink ? <OpenInNew1OutlineIcon size={15} /> : null
              }
              onClick={() => {
                trackPlaybookClickUpdate({
                  playbookId: id,
                });
                exeDeeplink('update', id, () => setShowPlaybookModal(true));
              }}
            />
          )}
          {!readOnly && duplicatePlaybookPermission && (
            <DropdownItem
              label="Duplicate"
              iconLeft={<DuplicateOutlineIcon size={20} />}
              onClick={() => {
                trackPlaybookClickDuplicate({
                  playbookId: id,
                });
                setShowDuplicateModal(true);
              }}
            />
          )}
          {!readOnly && movePlaybookPermission && (
            <DropdownItem
              label="Move to..."
              iconLeft={<SubdirectoryRightOutlineIcon size={18} />}
              onClick={() => setMoveModal(true)}
            />
          )}
          {!readOnly && flags.demoLink && (
            <>
              <Separator horizontal />
              <SubMenu
                width={205}
                triggerProps={{
                  label: 'Share',
                  iconRight: <RightOutlineIcon />,
                  iconLeft: <ShareOutlineIcon size={18} />,
                }}
              >
                <DropdownItem
                  label="Share internally"
                  onClick={() => {
                    setShowDemoLinkModal(true);
                  }}
                />
                <div ref={shareExternallyRef}>
                  <DropdownItem
                    disabled
                    label="Share externally"
                  />
                </div>
                <Tooltip
                  noArrow
                  placement="right"
                  anchorRef={shareExternallyRef}
                >
                  <div className="p-3">
                    <Typography
                      fontSize="text-ds-200"
                      lineHeight="leading-ds-200"
                      color="text-ds-natural"
                      fontWeight="font-ds-normal"
                      letterSpacing="tracking-ds-tight"
                      label="Playbooks can only be shared internally"
                    />
                  </div>
                </Tooltip>
              </SubMenu>
              <Separator horizontal />
            </>
          )}
          {!readOnly && deletePlaybookPermission && (
            <DropdownItem
              label="Archive"
              iconLeft={<ArchiveIcon size={20} />}
              onClick={() => {
                trackPlaybookClickArchive({
                  playbookId: id,
                });
                setShowArchiveModal(true);
              }}
            />
          )}
        </Dropdown>
      )}
      {showGiveADemoModal && (
        <GiveADemoFlowModal
          workspaceId={workspaceId}
          open={showGiveADemoModal}
          asset={{
            name,
            id,
            type: 'playbook',
            assets: playbookById.assets,
          }}
          onClose={setHideGiveADemoModal}
          onPresent={handleOnPresent}
          onSave={() => {
            navigate(`${rootPath}/playbook/${id}/presentations`);
            setHideGiveADemoModal();
          }}
          filesServiceUrl={filesServiceDomain}
        />
      )}
      {showDuplicateModal && (
        <DuplicatePlaybookModal
          workspaceId={workspaceId}
          playbookId={String(id)}
          show={showDuplicateModal}
          onClose={() => setShowDuplicateModal(false)}
          organizationId={organizationId}
        />
      )}
      {showArchiveModal && (
        <ArchivePlaybookModal
          workspaceId={workspaceId}
          folderId={playbookById.folderId}
          playbookId={String(id)}
          show={showArchiveModal}
          onClose={() => setShowArchiveModal(false)}
          organizationId={organizationId}
        />
      )}
      {moveModal && (
        <MovePlaybookModal
          playbook={playbookById}
          onClose={() => setMoveModal(false)}
          organizationId={organizationId}
          workspaceId={workspaceId}
        />
      )}
      {showPlaybookModal && (
        <UpdatePlaybookModal
          onClose={() => setShowPlaybookModal(false)}
          id={id}
        />
      )}

      {showDemoLinkModal && (
        <DemoLinkPlaybookModal
          workspaceId={workspaceId}
          assetId={playbook.id}
          assetType="playbook"
          organizationId={organizationId}
          isOpen={showDemoLinkModal}
          onClose={() => {
            setShowDemoLinkModal(false);
          }}
          role={userRole}
        />
      )}
    </div>
  );
};
